import type { AgendaPreview } from "@keepoinc/site-page-react-component";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { useAppState } from "../../contexts/App";
import { getPages, isAgendaPage } from "../../model/pages";
import { Site } from "../../model/site";
import { todayIn } from "../../pages/Agenda/timezone";
import { AgendaSettings, getAgenda, listServices, listSlots } from "../../services/agenda";

type Read = Pick<AgendaPreview, "services" | "days">;

/** The preview while the agenda is being read: nothing yet, and the page shows it's loading (not "no services"). */
const READING: AgendaPreview = { services: [], days: {}, loading: true };

// a day as a date at noon UTC: added to and read in UTC, it never slips to the day before
const addDays = (day: string, days: number) => {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

/** Whether each day (today in the agenda's timezone and the days ahead it takes) has hours: its week's, and not off. */
const daysOf = (settings: AgendaSettings): Record<string, boolean> => {
  const today = todayIn(settings.timezone);
  const days: Record<string, boolean> = {};
  for (let ahead = 0; ahead <= (settings.maxDaysAhead ?? 0); ahead++) {
    const day = addDays(today, ahead);
    const hours = settings.week?.[String(new Date(`${day}T12:00:00Z`).getUTCDay())] ?? [];
    days[day] = hours.length > 0 && !(settings.blockedDates ?? []).includes(day);
  }
  return days;
};

/**
 * What the Keepo's preview shows on its agenda page: the owner's own services, days and times, read from the API as
 * the owner registered them (nothing made up: the agenda starts empty). Read only when the Keepo has the agenda page,
 * and again when a screen changes (the services are changed on another one). While it's read the page shows as
 * loading; when it can't be read, as an empty agenda (nothing made up).
 */
export const useAgendaPreview = (site: Site | undefined): AgendaPreview | undefined => {
  const { appState } = useAppState();
  const siteId = appState.editingSiteId;
  const hasAgendaPage = !!site && getPages(site).some(isAgendaPage);
  const { pathname } = useLocation();
  // undefined: still reading; null: couldn't be read. Kept with its Keepo: another Keepo's never shows on this one
  const [reading, setRead] = useState<{ siteId: string; value: Read | null }>();
  const read = reading && reading.siteId === siteId ? reading.value : undefined;

  useEffect(() => {
    if (!siteId || !hasAgendaPage) {
      setRead(undefined);
      return;
    }
    // (on a new screen of the same Keepo the last reading stays on show until the new one comes)
    let current = true;
    Promise.all([listServices(siteId), getAgenda(siteId)])
      .then(([services, settings]) => {
        // the page shows the services that are on the agenda
        if (current) {
          setRead({ siteId, value: { services: services.filter(service => service.active), days: daysOf(settings) } });
        }
      })
      .catch(() => current && setRead({ siteId, value: null }));
    return () => {
      current = false;
    };
  }, [siteId, hasAgendaPage, pathname]);

  // the day's free times (the owner's grid also has the busy ones)
  const slots = useCallback(
    async (service: string, day: string) =>
      (await listSlots(siteId as string, service, day)).filter(slot => !slot.busy).map(slot => slot.time),
    [siteId]
  );

  return useMemo(() => {
    if (!hasAgendaPage) return undefined;
    if (read === undefined) return READING;
    return read ? { ...read, slots } : undefined;
  }, [hasAgendaPage, read, slots]);
};
